import { addMilliseconds, differenceInMilliseconds } from "date-fns";
import { createContext, useContext, useMemo } from "react";
import { defineMessage } from "react-intl";

export class ServerTimeUnknownError extends Error {
  constructor() {
    super("Server time not yet known");
    this.name = "ServerTimeUnknownError";
  }
}

export const isServerTimeUnknownError = (
  error: unknown,
): error is ServerTimeUnknownError => error instanceof ServerTimeUnknownError;

export const serverTimeUnknownMessage = defineMessage({
  id: "serverTime.unknown.error",
  defaultMessage:
    "This device hasn't synced a trusted time yet. Connect to the internet once, then try again.",
});

export const ServerTimeContext = createContext<{
  /**
   * Server-derived time, throwing `ServerTimeUnknownError` until this device
   * has completed one server exchange. Use it for every value that is
   * persisted — RxDB, GraphQL, `localStorage` — and let it throw: a wrong
   * persisted timestamp corrupts replication ordering.
   */
  getCurrentTime: () => Date;
  /**
   * Server-derived time, falling back to the untrusted device clock instead
   * of throwing. Render, filter and format only; if the value is written
   * anywhere it is `getCurrentTime`.
   */
  getDisplayTime: () => Date;
  setTime: (time: Date) => void;
}>({
  getCurrentTime: () => {
    throw new Error("ServerTimeProvider is not mounted");
  },
  getDisplayTime: () => new Date(),
  setTime: () => {},
});

type DateString = string & { __type: string };
const DEFAULT_STATE_KEY = "server-time-provider-state";
type State = {
  lastServerTime: DateString;
  localTimeAtLastServerTime: DateString;
};

const readOffsetMillis = (state: string): number | null => {
  try {
    const { lastServerTime, localTimeAtLastServerTime } = JSON.parse(
      state,
    ) as State;
    const offset = differenceInMilliseconds(
      new Date(lastServerTime),
      new Date(localTimeAtLastServerTime),
    );
    return Number.isNaN(offset) ? null : offset;
  } catch {
    return null;
  }
};

export const ServerTimeProvider: FCwC<{
  stateKey?: string;
  getLocalTime?: () => Date;
}> = ({
  children,
  stateKey = DEFAULT_STATE_KEY,
  getLocalTime = () => new Date(),
}) => {
  const value = useMemo(() => {
    const getCurrentTime = () => {
      const state = localStorage.getItem(stateKey);
      if (!state) {
        // Never get tempted to default to the local time. Having a trusted
        // server time is critical for auditing and should never be defaulted
        // as it would erase our trust in this value.
        throw new ServerTimeUnknownError();
      }
      const offset = readOffsetMillis(state);
      if (offset === null) {
        localStorage.removeItem(stateKey);
        throw new ServerTimeUnknownError();
      }
      return addMilliseconds(getLocalTime(), offset);
    };

    return {
      getCurrentTime,
      getDisplayTime: () => {
        try {
          return getCurrentTime();
        } catch {
          return getLocalTime();
        }
      },
      setTime: (serverTime: Date) => {
        localStorage.setItem(
          stateKey,
          JSON.stringify({
            lastServerTime: serverTime.toISOString(),
            localTimeAtLastServerTime: getLocalTime().toISOString(),
          }),
        );
      },
    };
  }, [getLocalTime, stateKey]);

  return (
    <ServerTimeContext.Provider value={value}>
      {children}
    </ServerTimeContext.Provider>
  );
};

export const useServerTime = () => {
  const context = useContext(ServerTimeContext);
  if (context === undefined) {
    throw new Error("useTimeContext must be used within a TimeProvider");
  }
  return context;
};
